import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Button, IconButton, TextField, Box, FormControl, InputLabel, Select, MenuItem, Typography, Menu, ListItemIcon, ListItemText } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

const B2BClientsTable = ({
  clients = [],
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  searchTerm,
  onSearchChange,
  balanceFilter,
  onBalanceFilterChange,
  totalRows,
  onAdd,
  onEdit,
  onView,
  onDelete,
  onSendPortalAccess,
  showPortal = false,
  onRefresh,
  loading
}) => {
  const { t } = useTranslation();
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [selectedActionClient, setSelectedActionClient] = useState(null);

  dayjs.extend(relativeTime);
  dayjs.locale('es');

  const handleOpenActions = (event, client) => {
    setActionsAnchorEl(event.currentTarget);
    setSelectedActionClient(client);
  };

  const handleCloseActions = () => {
    setActionsAnchorEl(null);
    setSelectedActionClient(null);
  };

  const handleAction = (callback) => {
    if (selectedActionClient && callback) callback(selectedActionClient);
    handleCloseActions();
  };

  const label = (key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };

  const columns = [
    { 
      field: 'nombreFantasia', 
      headerName: t('fantasy_name'), 
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    { 
      field: 'razonSocial', 
      headerName: t('business_name'), 
      flex: 2,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    { 
      field: 'contacto', 
      headerName: t('main_contact'), 
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    { 
      field: 'telefono', 
      headerName: t('phone'), 
      flex: .7,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    {
      field: 'commercialProfileName',
      headerName: 'Perfil comercial',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2" color={params.value ? 'text.primary' : 'text.secondary'}>
            {params.value || 'Sin perfil'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <IconButton
            onClick={(event) => handleOpenActions(event, params.row)}
            sx={{ borderRadius: '50%' }}
            aria-label={t('actions')}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Paper sx={{ padding: 2 }}>
      {/* Filtros */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
          <TextField
            label={t('search')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ width: 300 }}
            placeholder={t('search_by_name_rut')}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('balance_status')}</InputLabel>
            <Select
              value={balanceFilter}
              label={t('balance_status')}
              onChange={(e) => onBalanceFilterChange(e.target.value)}
            >
              <MenuItem value="all">{t('all')}</MenuItem>
              <MenuItem value="positive">{t('positive_balance')}</MenuItem>
              <MenuItem value="negative">{t('negative_balance')}</MenuItem>
              <MenuItem value="zero">{t('zero_balance')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <TableRefreshButton loading={loading} onClick={onRefresh} />
          <Button variant="outlined" startIcon={getIcon('add')} onClick={onAdd}>
            {t('add_b2b_client')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1000 }}>
          <DataGrid
            loading={loading}
            rows={clients}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            sortingMode="server"
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            filterMode="server"
            pageSizeOptions={[10, 25, 50]}
            paginationMode="server"
            rowCount={totalRows}
            getRowId={(row) => row.id || row.rut}
          />
        </Box>
      </Box>

      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={handleCloseActions}
      >
        <MenuItem onClick={() => handleAction(onView)}>
          <ListItemIcon>{getIcon('eye')}</ListItemIcon>
          <ListItemText primary={label('view', 'Ver')} />
        </MenuItem>
        <MenuItem onClick={() => handleAction(onEdit)}>
          <ListItemIcon>{getIcon('settings')}</ListItemIcon>
          <ListItemText primary={label('update', 'Modificar')} />
        </MenuItem>
        {showPortal && (
          <MenuItem
            onClick={() => handleAction(onSendPortalAccess)}
            disabled={!selectedActionClient?.email}
          >
            <ListItemIcon>{getIcon('mail')}</ListItemIcon>
            <ListItemText primary="Enviar acceso al portal B2B" />
          </MenuItem>
        )}
        <MenuItem onClick={() => handleAction(onDelete)}>
          <ListItemIcon>{getIcon('trash')}</ListItemIcon>
          <ListItemText primary={label('delete', 'Eliminar')} />
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default B2BClientsTable;
